import { action, computed, makeObservable, observable } from 'mobx';
import * as Sentry from '@sentry/react';
import * as yup from 'yup';
import { NotificationManager } from '../components/notification-manager';
import { HttpMethod, PaginatedList } from '../types';
import { RootStore } from './root';
import { KpiEnvelope, kpiEnvelopeSchema } from './kpi-envelope';
import { WithNetworkConcurrency } from './with-network-concurrency';

export type DateRange = {
    startDate: string;
    endDate: string;
};

export type ComparableDateRange = DateRange & {
    compareStartDate?: string;
    compareEndDate?: string;
};

/**
 * Max standardized-envelope requests in flight at once, across the whole app.
 * Sits below the dedup map, so it caps real API calls, not cards. 6 matches the
 * browser's typical per-host HTTP/1.1 connection limit: it smooths a dense
 * dashboard's mount burst (~12 distinct requests on the RPP page) into ~2 waves
 * without serializing kickoffs, so page TTI stays flat. Raising it trades burst
 * smoothing for a faster tail. It does not bound server load: an aborted call
 * frees its slot while the API finishes computing it.
 */
export const KPI_ENVELOPE_MAX_CONCURRENCY = 6;

/**
 * Per-request timeout (ms) for standardized-envelope calls. The concurrency gate
 * releases a slot only when a request settles, and the gate is an app-wide
 * singleton, so a stalled connection (a server holding the socket, not a 5xx)
 * would otherwise pin a slot forever: a handful of hung requests wedge KPI
 * loading on every page until reload. `makeNetworkCall` sets no timeout and
 * there is no global axios default, so this is the only bound. Generous enough
 * to clear a slow App Store Connect-backed response; only a genuinely hung
 * request should ever hit it.
 */
export const KPI_ENVELOPE_TIMEOUT_MS = 180000;

/**
 * Rejects an envelope request abandoned because every card waiting on it has
 * moved on (a filter or tab change), whether it was still queued or already in
 * flight. Those card hooks have already re-run (so they ignore this rejection);
 * the error exists so the promise settles rather than leaking, and so an
 * abandonment reads distinctly in a stack trace.
 */
export class KpiFetchAbandonedError extends Error {
    constructor() {
        super('KPI envelope request abandoned (no caller left)');
        this.name = 'KpiFetchAbandonedError';
    }
}

/**
 * Per-key gate bookkeeping, kept in lockstep with `inFlightEnvelopes`.
 * `liveWaiters` counts callers still waiting on the request (many share one
 * envelope key), so it is abandoned only once the last one aborts, never on the
 * first sibling to unmount. A caller that passed no signal is counted and never
 * released, so its request is pinned until it settles: one signal-less caller
 * makes the key un-abandonable for that request. `abandon` drops it from the
 * queue, or aborts it and returns its slot once it is in flight. `requestId`
 * identifies the registration itself, so every deferred mutation (cleanup,
 * release) can tell its own from the one that replaced it on the same key.
 */
interface EnvelopeControl {
    requestId: number;
    liveWaiters: number;
    abandon: () => void;
}

/**
 * Throwaway Taxi Driver campaign: the phone-numbers drill-down is a plain
 * paginated list (NOT a KPI envelope), so per SOC-37 it lives here as one thin,
 * boundary-validated fetch rather than inline in the container. Deleted whole
 * with the campaign (this block + `fetchCampaignPhoneNumbers` below).
 */
export interface TaxiPhoneRow {
    phoneNumber: string;
    visits: number;
    totalSpend: number;
    avgSpend: number;
}

export type TaxiPhoneResponse = PaginatedList<TaxiPhoneRow>;

export interface CampaignPhoneNumbersParams {
    campaignId: string;
    startDate: string;
    endDate: string;
    page: number;
    perPage: number;
}

/** Boundary schema for the phone-numbers response: a malformed 200 is rejected
 * (yields null) rather than rendered, so the table clears instead of throwing on
 * an absent `results` or a null numeric cell. */
const taxiPhoneResponseSchema = yup.object({
    page: yup.number().required(),
    perPage: yup.number().required(),
    total: yup.number().required(),
    results: yup
        .array(
            yup.object({
                phoneNumber: yup.string().required(),
                visits: yup.number().required(),
                totalSpend: yup.number().required(),
                avgSpend: yup.number().required(),
            })
        )
        .required(),
});

export type ServedCampaignKind = 'club' | 'edition';

export type ServedGameKind = 'rpp' | '31doc' | 'nhl-coins';

/** One campaign `GET /analytics/campaigns` serves. Dates are UTC days;
 * `endDate` is null while the campaign runs. `gameKind` is on editions only. */
export interface ServedCampaign {
    id: string;
    kind: ServedCampaignKind;
    gameKind?: ServedGameKind;
    label: string;
    startDate: string;
    endDate: string | null;
}

const servedCampaignsSchema = yup
    .array(
        yup.object({
            id: yup.string().required(),
            kind: yup
                .string<ServedCampaignKind>()
                .oneOf(['club', 'edition'])
                .required(),
            gameKind: yup
                .string<ServedGameKind>()
                .oneOf(['rpp', '31doc', 'nhl-coins']),
            label: yup.string().required(),
            startDate: yup.string().required(),
            endDate: yup.string().nullable().defined(),
        })
    )
    .required();

/** The two workbook reports served by `/api-cms/reports/<id>?format=xlsx`;
 * also the URL segment. */
export type WeeklyReportId = 'rpp-2026' | 'road-to-rewards-2026';

/** The API's error bodies are JSON, but nothing guarantees it on a proxy or
 * gateway response, so an unparseable body yields the caller's default. */
function errorMessageOf(body: string): string | undefined {
    try {
        return (JSON.parse(body) as { message?: string }).message;
    } catch {
        return undefined;
    }
}

export class AnalyticsStore extends WithNetworkConcurrency {
    /** In-flight envelope requests keyed by endpoint + params, so cards sharing
     * an identical request (same window/region) trigger a single API call.
     * Cleared when the request settles (dedup of concurrent calls, not a cache:
     * the shared hourly cache lives in the API, keyed the same way). */
    private inFlightEnvelopes = new Map<string, Promise<KpiEnvelope | null>>();
    /** Endpoints that already surfaced a drift toast, so one schema drift shows a
     * single toast instead of one per in-flight key (daily/total, compare, region)
     * and per remount, since failed envelopes aren't cached. Sentry still fires
     * every time; only the user-facing toast is deduped. */
    private envelopeErrorToasted = new Set<string>();
    /** Envelope requests waiting on a free concurrency slot (real API calls only;
     * dedup hits never enqueue). Plain, not observable: the bar reads the counts
     * below, never the queue. */
    private envelopeQueue: (() => void)[] = [];
    private envelopeInFlight = 0;
    /** Per-key gate bookkeeping (waiters, abandon), so a stale request, queued
     * or in flight, can be dropped on a filter change without harming a sibling
     * that still wants the shared envelope. Kept in lockstep with inFlightEnvelopes. */
    private envelopeControls = new Map<string, EnvelopeControl>();
    /** The served campaigns, read once per session: the registry changes with
     * a deploy, not between two visits to a page. A failed read is not kept. */
    private servedCampaigns?: Promise<ServedCampaign[] | null>;
    /** Hands every real request an identity, so a cleanup scheduled by one
     * registration cannot delete the registration that replaced it. */
    private envelopeRequestSeq = 0;
    /** Monotonic load-wave counter. A filter change bumps it (via ensureKpiWave),
     * so the bar attributes settles to the current wave only and a stale request
     * settling late cannot move it. `currentWaveKey` is the last page filter
     * identity seen, so the bump is idempotent across a page's cards. */
    private kpiGeneration = 0;
    private currentWaveKey: string | null = null;
    /** Load-bar counters for the current batch. `kpiLoadPercent` is clamped
     * monotonic so the fill never animates backwards when the total grows. */
    @observable kpiLoadTotal = 0;
    @observable kpiLoadSettled = 0;
    @observable kpiLoadPercent = 0;
    /** Reports currently downloading, keyed by report id, so a Road to Rewards
     * download cannot block an RPP one (or vice versa). */
    @observable private downloadingReports = new Set<WeeklyReportId>();
    rootStore: RootStore;

    constructor(rootStore: RootStore) {
        super();
        makeObservable(this);
        this.rootStore = rootStore;
    }

    /**
     * Signal a new load wave (a page filter change). Every card on the page calls
     * this from its fetch effect with the same page-level `waveKey`, so the first
     * call of a new wave bumps the generation and restarts the bar, and the rest
     * are no-ops. Bumping the bar is all it does: abandoning the stale wave's
     * requests is driven per card by the fetch AbortSignal, so a card that
     * pins its period (and never re-runs its effect, so never aborts) is left
     * alone rather than dropped. Pages that never call this stay on generation 0,
     * paced but never wave-reset.
     */
    @action ensureKpiWave(this: AnalyticsStore, waveKey: string): void {
        if (waveKey === this.currentWaveKey) {
            return;
        }
        this.currentWaveKey = waveKey;
        this.kpiGeneration += 1;
        // Restart the bar for the new wave. Requests still in flight from the old
        // wave settle under a stale generation and no longer count (see
        // onEnvelopeSettle), so the fill reflects the new filter honestly.
        this.kpiLoadTotal = 0;
        this.kpiLoadSettled = 0;
        this.kpiLoadPercent = 0;
    }

    /**
     * True while the current KPI load batch has unsettled requests. Drives the
     * progress bar's visibility.
     */
    @computed get kpiLoadActive(): boolean {
        return this.kpiLoadTotal > 0 && this.kpiLoadSettled < this.kpiLoadTotal;
    }

    /** Count a real request toward the load bar. A batch restarts only from idle
     * (nothing in flight or queued and the previous batch fully settled), so a
     * filter change after the page settled resets the bar; a change mid-load
     * extends the current batch instead, keeping `kpiLoadPercent` monotonic. */
    @action private countEnvelopeRequest(): void {
        const idle =
            this.envelopeInFlight === 0 &&
            this.envelopeQueue.length === 0 &&
            this.kpiLoadSettled >= this.kpiLoadTotal;
        if (idle) {
            this.kpiLoadTotal = 0;
            this.kpiLoadSettled = 0;
            this.kpiLoadPercent = 0;
        }
        this.kpiLoadTotal += 1;
    }

    /** Fire queued requests up to the concurrency cap. Only real API calls (dedup
     * misses) ever reach the queue, so the cap bounds requests, not cards. */
    @action private pumpEnvelopeQueue(): void {
        while (
            this.envelopeInFlight < KPI_ENVELOPE_MAX_CONCURRENCY &&
            this.envelopeQueue.length > 0
        ) {
            const fire = this.envelopeQueue.shift();
            if (fire === undefined) {
                return;
            }
            this.envelopeInFlight += 1;
            fire();
        }
    }

    @action private onEnvelopeSettle(generation: number): void {
        this.envelopeInFlight -= 1;
        // Only the current wave's requests move the bar. One that started under a
        // superseded wave (the filter changed while it was in flight) still
        // returns its slot, but must not advance a bar that already reset.
        if (generation === this.kpiGeneration && this.kpiLoadTotal > 0) {
            this.kpiLoadSettled += 1;
            this.kpiLoadPercent = Math.max(
                this.kpiLoadPercent,
                this.kpiLoadSettled / this.kpiLoadTotal
            );
        }
        this.pumpEnvelopeQueue();
    }

    /** An abandoned request never settles, so it leaves the current wave's
     * total instead of counting as settled. */
    @action private dropFromLoadBar(generation: number): void {
        if (generation !== this.kpiGeneration || this.kpiLoadTotal === 0) {
            return;
        }
        this.kpiLoadTotal -= 1;
        if (this.kpiLoadTotal > 0) {
            this.kpiLoadPercent = Math.max(
                this.kpiLoadPercent,
                this.kpiLoadSettled / this.kpiLoadTotal
            );
        }
    }

    @action private onEnvelopeAbort(generation: number): void {
        this.envelopeInFlight -= 1;
        this.dropFromLoadBar(generation);
        this.pumpEnvelopeQueue();
    }

    /** Drops the key's registry entries, but only while they still belong to
     * `requestId`. An abandoned request's deferred cleanup lands one microtask
     * after a same-key refetch registered, and must be a no-op then. */
    private cleanupEnvelopeKey(key: string, requestId: number): void {
        if (this.envelopeControls.get(key)?.requestId !== requestId) {
            return;
        }
        this.inFlightEnvelopes.delete(key);
        this.envelopeControls.delete(key);
    }

    /** Wire an aborting caller to the gate: when the last live caller of a
     * request aborts, abandon it, queued or in flight. Same-key cards abort in lockstep
     * on a filter change (identical params), so this drops the stale wave without
     * harming a sibling that still wants the shared envelope. */
    private attachAbandonOnAbort(key: string, signal: AbortSignal): void {
        // Bind the release to the registration that exists NOW. A caller can
        // abort after its request settled and a same-key refetch registered;
        // that late abort must not touch the newcomer's waiter count.
        const requestId = this.envelopeControls.get(key)?.requestId;
        if (requestId === undefined) {
            return;
        }
        if (signal.aborted) {
            this.releaseEnvelopeWaiter(key, requestId);
            return;
        }
        signal.addEventListener(
            'abort',
            () => this.releaseEnvelopeWaiter(key, requestId),
            { once: true }
        );
    }

    @action private releaseEnvelopeWaiter(
        key: string,
        requestId: number
    ): void {
        const control = this.envelopeControls.get(key);
        if (!control || control.requestId !== requestId) {
            return;
        }
        control.liveWaiters -= 1;
        if (control.liveWaiters <= 0) {
            control.abandon();
        }
    }

    /**
     * Fetches any KPI that speaks the standardized envelope. `endpoint` is the
     * path under /api-cms; `params` are the uniform query params (startDate,
     * endDate, granularity, optional provinces). Validated against the shared
     * envelope schema. Concurrent calls with an identical endpoint + params
     * share one request, so cards on the same window/region hit the API once.
     *
     * Real requests (dedup misses) are paced by a concurrency gate so a dense
     * dashboard's mount burst does not fan out all at once. The gate sits BELOW
     * the dedup map: same-key cards ride the shared promise and never take a
     * slot, so the cap bounds real API calls, not cards, and pacing can never
     * multiply requests to an externally rate-limited endpoint.
     *
     * Pass `signal` (a card's per-fetch AbortSignal) to make the request
     * abandonable: when the last live caller aborts (a filter or tab change
     * re-ran or unmounted the card), a queued request is dropped instead of
     * firing, and an in-flight one is aborted and gives its slot to the page now
     * shown. The API still finishes and caches it. A request is dropped only
     * once every same-key caller has aborted, so a sibling still wanting the
     * shared envelope is safe.
     *
     * A caller that passes no signal is still counted as a waiter and has no way
     * to be released, so it pins the request until it settles: the request can
     * no longer be abandoned by anyone. Every caller should pass a signal; the
     * omission is tolerated, not free.
     */
    fetchKpiEnvelope(
        this: AnalyticsStore,
        endpoint: string,
        params: Record<string, string>,
        signal?: AbortSignal
    ): Promise<KpiEnvelope | null> {
        const key = `${endpoint}?${new URLSearchParams(
            Object.entries(params).sort(([a], [b]) => (a < b ? -1 : 1))
        ).toString()}`;

        const inFlight = this.inFlightEnvelopes.get(key);
        if (inFlight) {
            // Dedup: ride the shared request. Register this caller too, so the
            // request is abandoned only once EVERY live caller has aborted, never
            // on the first sibling to unmount.
            const shared = this.envelopeControls.get(key);
            if (shared) {
                // A signal-less caller counts as a waiter too. It has no way to
                // decrement it later, so it keeps the request alive until it
                // settles instead of ever aborting.
                shared.liveWaiters += 1;
                if (signal) {
                    this.attachAbandonOnAbort(key, signal);
                }
            }
            return inFlight;
        }

        // A real request. Count it and register its promise now, so same-key
        // callers that arrive while it is still QUEUED dedup onto it too. Capture
        // the wave it belongs to, so a settle after a filter change does not move
        // the reset bar.
        const generation = this.kpiGeneration;
        const requestId = (this.envelopeRequestSeq += 1);
        this.countEnvelopeRequest();
        let resolveOuter!: (value: KpiEnvelope | null) => void;
        let rejectOuter!: (error: unknown) => void;
        const outer = new Promise<KpiEnvelope | null>((resolve, reject) => {
            resolveOuter = resolve;
            rejectOuter = reject;
        });
        this.inFlightEnvelopes.set(key, outer);
        // Clear the dedup key once settled (dedup of concurrent calls, not a
        // cache). Both arms delete, so a rejected request is retried on remount.
        void outer.then(
            () => this.cleanupEnvelopeKey(key, requestId),
            () => this.cleanupEnvelopeKey(key, requestId)
        );

        const transport = new AbortController();
        let started = false;
        let slotReturned = false;

        const fire = () => {
            started = true;
            let request: Promise<KpiEnvelope | null>;
            try {
                request = this.rootStore
                    .makeNetworkCall<unknown>({
                        method: HttpMethod.GET,
                        url: endpoint,
                        params,
                        signal: transport.signal,
                        // Bound the request so a stalled response returns its
                        // slot instead of wedging the app-wide gate (see
                        // KPI_ENVELOPE_TIMEOUT_MS). A timeout resolves through
                        // makeNetworkCall as { data: null }, so it settles the
                        // slot on the success arm below like any empty body.
                        timeout: KPI_ENVELOPE_TIMEOUT_MS,
                    })
                    .then((response) => {
                        if (response.data == null) {
                            return null;
                        }
                        // Validate at the boundary against the vendored zod schema (the
                        // same definition the KpiEnvelope type is inferred from); a
                        // drifted/invalid body yields null rather than a mistyped object.
                        const parsed = kpiEnvelopeSchema.safeParse(
                            response.data
                        );
                        if (!parsed.success) {
                            // A 200 body that fails the envelope contract means the
                            // vendored schema has drifted from the backend. safeParse
                            // swallows the throw, so surface it ourselves instead of
                            // blanking the dashboard silently: Sentry for ops, a toast
                            // so the user knows the data could not be loaded.
                            console.error('Invalid KPI envelope', parsed.error);
                            Sentry.captureException(parsed.error, {
                                tags: { feature: 'kpi-envelope' },
                                extra: { endpoint },
                            });
                            // Dedupe the toast per endpoint: a drift stacks several
                            // near-identical toasts otherwise (one per in-flight key,
                            // re-fired on every remount/region change).
                            if (!this.envelopeErrorToasted.has(endpoint)) {
                                this.envelopeErrorToasted.add(endpoint);
                                NotificationManager.showError(
                                    'Analytics data could not be loaded (unexpected format).'
                                );
                            }
                            return null;
                        }
                        return parsed.data;
                    });
            } catch (error) {
                // A synchronous throw building the request (never a rejected
                // promise) must still return its slot, or the concurrency cap
                // leaks and the gate eventually stalls. Settle on a microtask so
                // we never re-enter the pump mid-loop.
                slotReturned = true;
                rejectOuter(error);
                void Promise.resolve().then(() =>
                    this.onEnvelopeSettle(generation)
                );
                return;
            }
            request.then(
                (value) => {
                    if (slotReturned) return;
                    slotReturned = true;
                    this.onEnvelopeSettle(generation);
                    resolveOuter(value);
                },
                (error) => {
                    if (slotReturned) return;
                    slotReturned = true;
                    this.onEnvelopeSettle(generation);
                    rejectOuter(error);
                }
            );
        };

        const abortInFlight = () => {
            const control = this.envelopeControls.get(key);
            if (
                slotReturned ||
                control?.requestId !== requestId ||
                control.liveWaiters > 0
            ) {
                return;
            }
            slotReturned = true;
            transport.abort();
            this.cleanupEnvelopeKey(key, requestId);
            this.onEnvelopeAbort(generation);
            rejectOuter(new KpiFetchAbandonedError());
        };

        // Drop a request whose every caller has moved on, and reject its
        // promise (the waiting cards have re-run and ignore it). The dedup key
        // goes with it, so the next caller sends a fresh request.
        const abandon = () => {
            if (started) {
                // A microtask lets the rest of a React cleanup pass run first:
                // sibling cards drop their queued requests before this slot
                // pumps one, and a remount on the same key keeps the request.
                void Promise.resolve().then(abortInFlight);
                return;
            }
            const index = this.envelopeQueue.indexOf(fire);
            if (index === -1) {
                return;
            }
            this.envelopeQueue.splice(index, 1);
            this.cleanupEnvelopeKey(key, requestId);
            this.dropFromLoadBar(generation);
            rejectOuter(new KpiFetchAbandonedError());
        };

        this.envelopeControls.set(key, {
            requestId,
            liveWaiters: 1,
            abandon,
        });
        // Wire the abort before pumping, so an already-aborted signal drops the
        // request from the queue instead of sending it.
        this.envelopeQueue.push(fire);
        if (signal) {
            this.attachAbandonOnAbort(key, signal);
        }
        this.pumpEnvelopeQueue();
        return outer;
    }

    /**
     * Fetch one page of a campaign's per-phone drill-down (`campaign-analytics/
     * phone-numbers`), a plain paginated GET validated at the boundary. Returns
     * `null` on a failed or mis-shaped response so the caller clears the table
     * rather than rendering stale or malformed rows. Part of the throwaway Taxi
     * campaign (deleted with it).
     */
    async fetchCampaignPhoneNumbers(
        this: AnalyticsStore,
        params: CampaignPhoneNumbersParams
    ): Promise<TaxiPhoneResponse | null> {
        const response = await this.rootStore.makeNetworkCall(
            {
                method: HttpMethod.GET,
                url: 'campaign-analytics/phone-numbers',
                params,
            },
            taxiPhoneResponseSchema
        );
        return response.data ?? null;
    }

    /** The campaigns `GET /analytics/campaigns` serves, or `null` when the
     * read failed or the body did not match the contract. */
    fetchServedCampaigns(
        this: AnalyticsStore
    ): Promise<ServedCampaign[] | null> {
        if (this.servedCampaigns) {
            return this.servedCampaigns;
        }
        const request = this.rootStore
            .makeNetworkCall(
                { method: HttpMethod.GET, url: 'analytics/campaigns' },
                servedCampaignsSchema
            )
            .then((response) => response.data ?? null);
        this.servedCampaigns = request;
        void request.then((campaigns) => {
            if (campaigns === null && this.servedCampaigns === request) {
                this.servedCampaigns = undefined;
            }
        });
        return request;
    }

    /** True while `reportId`'s download is in flight. Per-report, so the RPP
     * and Road to Rewards buttons each show only their own request. */
    isDownloadingReport(
        this: AnalyticsStore,
        reportId: WeeklyReportId
    ): boolean {
        return this.downloadingReports.has(reportId);
    }

    /**
     * Never reads the page's period/region filters: always the unfiltered
     * workbook. `fallbackFilenameBase` names the file only when the response
     * has no `content-disposition` header; the store appends the date and
     * extension itself.
     */
    async downloadWeeklyReport(
        this: AnalyticsStore,
        reportId: WeeklyReportId,
        fallbackFilenameBase: string
    ): Promise<void> {
        if (this.downloadingReports.has(reportId)) {
            return;
        }

        this.downloadingReports.add(reportId);

        try {
            const baseUrl = import.meta.env.REACT_APP_API_URL || '';
            const url = `${baseUrl}/api-cms/reports/${reportId}?format=xlsx`;
            const headers = this.rootStore.getHeaders();

            const response = await fetch(url, {
                method: 'GET',
                headers,
            });

            if (!response.ok) {
                const errorText = await response.text();
                if (response.status === 401) {
                    this.rootStore.handleUnauthorized(
                        errorMessageOf(errorText)
                    );
                    return;
                }
                throw new Error(
                    `Weekly report download failed with status: ${response.status} - ${errorText}`
                );
            }

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);

            const contentDisposition = response.headers.get(
                'content-disposition'
            );
            const filenameMatch = contentDisposition?.match(
                /filename="([^"]+)"|filename=([^;]+)/
            );
            const filename = filenameMatch
                ? filenameMatch[1] ?? filenameMatch[2]
                : `${fallbackFilenameBase} - ${new Date()
                      .toISOString()
                      .slice(0, 10)}.xlsx`;

            const link = document.createElement('a');
            link.style.display = 'none';
            link.href = downloadUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();

            // Deferred like `exportPromotions`: some browsers have not started
            // reading the blob when `click()` returns, and revoking early
            // leaves them with an empty file.
            setTimeout(() => {
                window.URL.revokeObjectURL(downloadUrl);
                document.body.removeChild(link);
            }, 100);
        } catch (error) {
            console.error('Weekly report download error:', error);
            NotificationManager.showError(
                error instanceof Error
                    ? `Failed to download weekly report: ${error.message}`
                    : 'Failed to download weekly report'
            );
        } finally {
            this.downloadingReports.delete(reportId);
        }
    }
}
